/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface MedicalUnitProps {
  id: string;
  name: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class MedicalUnitRecord extends Record<MedicalUnitProps>({
  id: null as any,
  name: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): MedicalUnitRecord {
    if (json == null) {
      throw new Error('MedicalUnitRecord.fromJS: parameter cannot be null');
    }
    return new MedicalUnitRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'MedicalUnitJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'MedicalUnitJson.name'),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [MedicalUnitRecord, Error[]] {
    if (json == null) {
      throw new Error('MedicalUnitRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new MedicalUnitRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'MedicalUnitJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'MedicalUnitJson.name'),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: MedicalUnitProps) {
    super(props);
    if (props == null) {
      throw new Error('MedicalUnitRecord constructor parameter cannot be null');
    }
  }
}

export abstract class MedicalUnitViewModelBase implements MedicalUnitProps {
  protected readonly _props: MedicalUnitProps;
  private _json?: MedicalUnitProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: MedicalUnitProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllMedicalUnitEntities = (state: AppState) =>
  state.entityStores.medicalUnit.entities;

export const selectMedicalUnitById = (id: string) => (state: AppState) =>
  state.entityStores.medicalUnit.entities.get(id);

export const selectMedicalUnitMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.medicalUnit.meta.get(id);

export const selectMedicalUnitAndMetadataById = (id: string) =>
  createSelector(
    selectMedicalUnitById(id),
    selectMedicalUnitMetadataById(id),
    (medicalUnit, meta) => ({ medicalUnit, meta }),
  );

export function selectMedicalUnitEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllMedicalUnitEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, MedicalUnitRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectMedicalUnitEntitiesByName(name: string) {
  return createSelector(selectAllMedicalUnitEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}
//#endregion Selectors
